'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { GeneralModal, IconBox } from '@/components/ui/general-modal'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Beaker, Zap, Plus, Loader2, X } from 'lucide-react'
import { useAddAdToPoolMutation, useRemoveAdFromPoolMutation } from '@/api/research-jobs/ads/pool-management-mutations'
import { toast } from '@/features/toast/toast'
import { cn } from '@/utils/styles/cn'

const POOL_OPTIONS = [
    {
        key: 'TRENDING' as const,
        label: 'Trending',
        icon: Zap,
        badgeVariant: 'destructive' as const,
    },
]

const CATEGORY_OPTIONS = [
    { value: 'VIDEO_ADS', label: 'Video Ads' },
    { value: 'IMAGE_ADS', label: 'Image Ads' },
]

export function AdPoolBadges({ pools, adUuid, className }: { pools: any[]; adUuid: string; className?: string }) {
    const removeFromPool = useRemoveAdFromPoolMutation()

    const currentPools = pools || []

    const handleRemoveFromPool = async (poolKey: (typeof POOL_OPTIONS)[0]['key']) => {
        const promise = removeFromPool.mutateAsync({
            ad_uuid: adUuid,
            pool_key: poolKey,
        })

        toast.promise(promise, {
            loading: 'Removing from pool...',
            success: `Removed from ${POOL_OPTIONS.find((p) => p.key === poolKey)?.label} pool`,
            error: 'Failed to remove from pool',
        })

        try {
            await promise
        } catch (error) {
            console.error('Error removing from pool:', error)
        }
    }

    return (
        <TooltipProvider delayDuration={0}>
            <div className={cn('flex items-center gap-2', className)}>
                {currentPools.map((pool: any) => {
                    const poolOption = POOL_OPTIONS.find((p) => p.key === pool.key)
                    if (!poolOption) return null

                    const Icon = poolOption.icon
                    const badge = (
                        <Badge
                            key={pool.key}
                            variant={poolOption.badgeVariant}
                            className="flex items-center gap-1 text-xs pr-1 group hover:pr-1"
                        >
                            <Icon className="h-3 w-3" />
                            <span>{poolOption.label}</span>
                            {pool.category && (
                                <span className="text-xs opacity-70">
                                    ({CATEGORY_OPTIONS.find((c) => c.value === pool.category)?.label})
                                </span>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemoveFromPool(pool.key)
                                }}
                                className="ml-1 hover:bg-black/10 rounded-sm p-0.5 opacity-60 hover:opacity-100 transition-opacity"
                                title={`Remove from ${poolOption.label} pool`}
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    )

                    if (pool.description) {
                        return (
                            <Tooltip key={pool.key} delayDuration={0}>
                                <TooltipTrigger asChild>{badge}</TooltipTrigger>
                                <TooltipContent>
                                    <p className="max-w-xs">{pool.description}</p>
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    return badge
                })}
            </div>
        </TooltipProvider>
    )
}

export function AdPoolButton({
    pools,
    adUuid,
    className,
    trigger,
}: {
    pools: any[]
    adUuid: string
    className?: string
    /** Replaces the default full-width button trigger, e.g. with a card-overlay glass button. */
    trigger?: (state: { open: boolean }) => React.ReactNode
}) {
    const addToPool = useAddAdToPoolMutation()
    const [isOpen, setIsOpen] = useState(false)
    const [selectedPoolForAdd, setSelectedPoolForAdd] = useState<string | null>(null)
    const [selectedCategory, setSelectedCategory] = useState<string>('')
    const [description, setDescription] = useState<string>('')
    const [isDialogOpen, setIsDialogOpen] = useState(false)

    const currentPools = pools || []
    const currentPoolKeys = new Set(currentPools.map((p: any) => p.key))

    const handleAddToPool = async (
        poolKey: (typeof POOL_OPTIONS)[0]['key'],
        category?: string,
        description?: string
    ) => {
        const promise = addToPool.mutateAsync({
            ad_uuid: adUuid,
            pool_key: poolKey,
            ...(category && { category: category as any }),
            ...(description && { description }),
        })

        toast.promise(promise, {
            loading: 'Adding to pool...',
            success: `Added to ${POOL_OPTIONS.find((p) => p.key === poolKey)?.label} pool`,
            error: 'Failed to add to pool',
        })

        try {
            await promise
            setIsDialogOpen(false)
            setSelectedPoolForAdd(null)
            setSelectedCategory('')
            setDescription('')
        } catch (error) {
            console.error('Error adding to pool:', error)
        }
    }

    const handleAddPoolClick = (poolKey: string) => {
        setSelectedPoolForAdd(poolKey)
        setIsDialogOpen(true)
        setIsOpen(false)
    }

    return (
        <>
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild>
                    {trigger ? (
                        trigger({ open: isOpen })
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            className={cn('w-full flex items-center gap-1', className)}
                            disabled={addToPool.isPending}
                        >
                            {addToPool.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Beaker className="w-4 h-4" />
                            )}
                            <span>Pools</span>
                        </Button>
                    )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[160px]">
                    {POOL_OPTIONS.filter((poolOption) => !currentPoolKeys.has(poolOption.key)).map((poolOption) => {
                        const Icon = poolOption.icon

                        return (
                            <DropdownMenuItem
                                key={poolOption.key}
                                onClick={() => handleAddPoolClick(poolOption.key)}
                                className="text-xs"
                            >
                                <div className="flex items-center gap-2 flex-1">
                                    <Icon className="h-3 w-3" />
                                    <span>Add to {poolOption.label}</span>
                                </div>
                                <Plus className="h-3 w-3" />
                            </DropdownMenuItem>
                        )
                    })}
                    {POOL_OPTIONS.filter((poolOption) => !currentPoolKeys.has(poolOption.key)).length === 0 && (
                        <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                            Already in all pools
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <GeneralModal
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                title={`Add to ${selectedPoolForAdd ? POOL_OPTIONS.find((p) => p.key === selectedPoolForAdd)?.label : ''} Pool`}
                subtitle="Configure category and description for this pool entry"
                icon={
                    <IconBox>
                        <Plus className="h-5 w-5 text-[#6F6F6F]" />
                    </IconBox>
                }
                className="md:w-md"
            >
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="category">Category (optional)</Label>
                        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select a category" />
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORY_OPTIONS.map((category) => (
                                    <SelectItem key={category.value} value={category.value}>
                                        {category.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="description">Description (optional)</Label>
                        <Textarea
                            id="description"
                            placeholder="Add a description for this pool entry..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="min-h-[80px]"
                        />
                    </div>
                    <div className="flex justify-end space-x-2">
                        <Button variant="ds2-secondary" size="ds2" onClick={() => setIsDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="ds2-primary"
                            size="ds2"
                            onClick={() => {
                                if (selectedPoolForAdd) {
                                    handleAddToPool(
                                        selectedPoolForAdd as any,
                                        selectedCategory || undefined,
                                        description || undefined
                                    )
                                }
                            }}
                            disabled={addToPool.isPending}
                        >
                            {addToPool.isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                'Add to Pool'
                            )}
                        </Button>
                    </div>
                </div>
            </GeneralModal>
        </>
    )
}
